import get from 'lodash/get';
import find from 'lodash/find';
import flow from 'lodash/flow';
import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { SubmissionError } from 'redux-form';

import PaymentInformationHeader from '../subscription/form/PaymentInformationHeader';
import FakeAuthViewContainer from '../auth/FakeAuthViewContainer';
import OkDialog from '../shared/dialog/OkDialog';
import AuthAndPaymentFlowHeader from '../shared/AuthAndPaymentFlowHeader';

import { logSubscribeActivity, logClientError } from '../../actions/logging';
import { clearFakeAuthData } from '../../actions/fakeAuth';

import getSubscriptionError from '../../utils/subscription/getSubscriptionError';
import clientRedirect from '../../utils/clientRedirect';
import generateOriginLabelFromQuery from '../../utils/generateOriginLabelFromQuery';

import { PAYMENT_COMPLETE_PATH, PAYMENT_PATH } from '../../constants/paths';
import { SIGN_IN_DEEPLINK } from '../../constants/mobileAppDeepLinks';
import { ONAIR_ADD_STATION_FORM_URL } from '../../constants/cmsLinks';
import {
  SELECTED_SKU_QUERY_PARAM,
  NO_PAYMENT_SKU,
  IMMEDIATE_REDIRECT_QUERY_PARAM,
  REDIRECT_URI_QUERY_PARAM,
} from '../../constants/queryParams';
import {
  SOURCE_MOBILE_UPSELL,
  SOURCE_ONAIR,
} from '../../constants/sourceQueryParams';

import { PAYMENT_DEFAULT_ERROR } from '../../constants/localizations/subscription';

import paymentFlows from '../../constants/payment/paymentFlows';
import trackingLog from '../../constants/payment/tracking';
import isPaymentRequired from '../utils/isPaymentRequired';
import withAnimationControl from '../../decorators/withAnimationControl';
import AuthSuccessText from '../auth/shared/AuthSuccessText';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import PaymentSuccessDialog from './PaymentSuccessDialog';
import NoPaymentFlow from './NoPaymentFlow';
import PaymentOptionsWrapper from './PaymentOptionsWrapper';
import PaymentFlowFormLayout from './PaymentFlowFormLayout';

/**
 * A reusable base PaymentForm class that renders a form element with success and failure dialogs.
 * The PaymentForm takes both a custom onSubmit and the redux-form handleSubmit function. This
 * component is responsible for calling the onSubmit handler when the form is submitted and
 * managing the submission processing (opening/closing dialogs, disabling form, etc.).
 */
class PaymentForm extends Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
    breakpoint: PropTypes.number.isRequired,
    successDialogContent: PropTypes.string.isRequired,
    defaultErrorMessage: PropTypes.string.isRequired,
    failureDialogContent: PropTypes.string,
    redirectOnSuccess: PropTypes.bool.isRequired,
    altSuccessDialogHeading: PropTypes.object,
    products: PropTypes.array, // not provided by Payment Information
    billingInfo: PropTypes.object,
    hideFooterCopy: PropTypes.bool,
    paymentFlowLayoutProps: PropTypes.object.isRequired,
    defaultSubmitButtonLabel: PropTypes.string.isRequired,
    formId: PropTypes.string.isRequired,
    onSubmit: PropTypes.func.isRequired,
    header: PropTypes.string.isRequired,
    subheader: PropTypes.string.isRequired,
    // from withAnimationControl
    isRecurlyLoading: PropTypes.bool.isRequired,
    setRecurlyLoadingState: PropTypes.func.isRequired,
    startFakeAuthTransition: PropTypes.func.isRequired,
    // from redux-form
    form: PropTypes.object.isRequired,
    error: PropTypes.object,
    submitting: PropTypes.bool,
  };

  static contextType = LocationAndLocalizationContext;

  constructor(props, context) {
    super(props, context);
    const { location } = context;
    const querySkuFromUrl = get(location, ['query', SELECTED_SKU_QUERY_PARAM]);

    const selectedProductSku =
      querySkuFromUrl || get(props.products, '[0].sku');
    const selectedProduct = find(
      props.products,
      (product) => product.sku === selectedProductSku,
    );
    const isNoPaymentFlow = !isPaymentRequired(selectedProduct, location);

    this.state = {
      selectedProduct,
      selectedProductSku,
      failureMessage: '',
      currentPaymentFlow: paymentFlows.creditCard,
      isFormDisabled: false,
      showSuccessDialog: false,
      showFailureDialog: false,
      hasRecurlyError: false,
      isNoPaymentFlow,
    };
    this.onSubmitFailure = this.onSubmitFailure.bind(this);
    this.setSelectedProductSku = this.setSelectedProductSku.bind(this);
    this.disableCreditCardForm = this.disableCreditCardForm.bind(this);
    this.handlePaymentFlowChange = this.handlePaymentFlowChange.bind(this);
    this.onSubmitSuccess = this.onSubmitSuccess.bind(this);
    this.openSuccessDialog = this.openSuccessDialog.bind(this);
    this.closeSuccessDialog = this.closeSuccessDialog.bind(this);
    this.openFailureDialog = this.openFailureDialog.bind(this);
    this.closeFailureDialog = this.closeFailureDialog.bind(this);
    this.setRecurlyErrorState = this.setRecurlyErrorState.bind(this);
  }

  /**
   * onSubmitSuccess function opens the success dialog when onSubmit is executed successfully
   */
  onSubmitSuccess() {
    const { history, redirectOnSuccess, actions } = this.props;
    const { isNoPaymentFlow, selectedProductSku } = this.state;
    const { location } = this.context;
    const { query } = location;

    actions.clearFakeAuthData();

    const querySource = generateOriginLabelFromQuery(location);
    if (querySource.indexOf(SOURCE_MOBILE_UPSELL) > -1) {
      clientRedirect(SIGN_IN_DEEPLINK);
    } else if (querySource.indexOf(SOURCE_ONAIR) > -1) {
      clientRedirect(ONAIR_ADD_STATION_FORM_URL);
    } else if (
      redirectOnSuccess &&
      query[IMMEDIATE_REDIRECT_QUERY_PARAM] === 'true' &&
      query[REDIRECT_URI_QUERY_PARAM]
    ) {
      clientRedirect(query[REDIRECT_URI_QUERY_PARAM]);
    } else if (redirectOnSuccess) {
      let search = location.search || '';
      if (isNoPaymentFlow) {
        search += `&${NO_PAYMENT_SKU}=${selectedProductSku}`;
      }
      history.replace({
        pathname: PAYMENT_COMPLETE_PATH,
        search,
      });
    } else {
      this.openSuccessDialog();
    }
  }

  /**
   * onSubmitFailure function opens the failure dialog when onSubmit throws an error. Is
   * also responsible for setting the error message of the form, by throwing the SubmissionError,
   * and setting the failure message of the failure dialog.
   * @param error (Error|SubmissionError)
   * @param {string} [alternateErrorMessage]
   */
  onSubmitFailure(error, alternateErrorMessage) {
    const { actions, formId } = this.props;
    const { getLocalizedText } = this.context;

    // log the client error
    actions.logClientError({
      message: `${trackingLog.labels.paymentFormError}.${trackingLog.action.submit}`,
      context: {
        error: get(error, 'message'),
        stack: get(error, 'stack'),
      },
    });

    // get failure message
    const { failureMessage, failedAction, failedLabel } =
      getSubscriptionError(error, formId) || {};

    const failureMessageToUse =
      alternateErrorMessage ||
      getLocalizedText(failureMessage || PAYMENT_DEFAULT_ERROR);

    this.setState({
      isFormDisabled: false, // re-enable form
      currentPaymentFlow: paymentFlows.creditCard, // re-enable form
    });

    // open the failed dialog and set message
    this.openFailureDialog(failureMessageToUse);

    // log the failure
    actions.logSubscribeActivity(failedAction, failedLabel);

    // throw the SubmissionError to cancel form submission
    throw new SubmissionError({
      _error: {
        base: failureMessageToUse,
      },
    });
  }

  /**
   * setSelectedProductSku is called by the radio buttons
   * the sku is used to set selectedProduct in the render function
   * and is passed down into the components that need it.
   */
  setSelectedProductSku(sku) {
    const selectedProduct = find(
      this.props.products,
      (product) => product.sku === sku,
    );
    this.setState({
      selectedProductSku: sku,
      selectedProduct,
    });
  }

  setRecurlyErrorState(hasError) {
    if (hasError !== this.state.hasRecurlyError) {
      this.setState({
        hasRecurlyError: hasError,
      });
    }
  }

  /**
   * disableCreditCardForm is called within the CreditCard
   * submit function while it's processing.
   */
  disableCreditCardForm() {
    // disable form
    this.setState({
      isFormDisabled: true,
    });
  }

  /**
   * handlePaymentFlowChange is used to pass down the type of
   * payment the user is in the middle of, to  enable/disable
   * the credit card form and enable/disable the Paypal in progress UI.
   */
  handlePaymentFlowChange(updatedFlow) {
    if (updatedFlow === this.state.currentPaymentFlow) {
      return;
    }

    this.setState({
      currentPaymentFlow: updatedFlow || paymentFlows.creditCard,
    });
  }

  openSuccessDialog() {
    this.setState({ showSuccessDialog: true });
  }

  closeSuccessDialog() {
    this.setState({ showSuccessDialog: false });
    clientRedirect(PAYMENT_PATH);
  }

  openFailureDialog(failureMessage) {
    this.setState({
      failureMessage,
      showFailureDialog: true,
    });
  }

  closeFailureDialog() {
    this.setState({ showFailureDialog: false });
  }

  render() {
    const {
      formId,
      successDialogContent,
      failureDialogContent,
      defaultErrorMessage,
      altSuccessDialogHeading,
      billingInfo,
      paymentFlowLayoutProps,
      actions,
      header,
      subheader,
      isRecurlyLoading,
      setRecurlyLoadingState,
      startFakeAuthTransition,
    } = this.props;

    const {
      showSuccessDialog,
      showFailureDialog,
      failureMessage,
      selectedProductSku,
      currentPaymentFlow,
      isFormDisabled,
      selectedProduct,
      isNoPaymentFlow,
      hasRecurlyError,
    } = this.state;

    const { getLocalizedText } = this.context;

    const failureMessageToUse =
      failureMessage || failureDialogContent || defaultErrorMessage;
    const shouldShowHeader = hasRecurlyError ? !hasRecurlyError : true;

    const layoutProps = {
      ...paymentFlowLayoutProps,
      selectedProduct,
      isNoPaymentFlow,
      billingInfo,
    };

    const paymentProps = {
      currentPaymentFlow,
      selectedProduct,
      setRecurlyLoadingState,
      handlePaymentFlowChange: this.handlePaymentFlowChange,
      onSubmit: this.props.onSubmit,
      onSubmitSuccess: this.onSubmitSuccess,
      onSubmitFailure: this.onSubmitFailure,
      openFailureDialog: this.openFailureDialog,
      setRecurlyErrorState: this.setRecurlyErrorState,
    };

    return (
      <PaymentFlowFormLayout {...layoutProps}>
        <div>
          {' '}
          {/* this div is used to control the animation, see withAnimationControl */}
          <FakeAuthViewContainer
            startFakeAuthTransition={startFakeAuthTransition}
            isRecurlyLoading={isRecurlyLoading}
          />
          <AuthSuccessText isRecurlyLoading={isRecurlyLoading} />
          {isNoPaymentFlow ? (
            <NoPaymentFlow {...paymentProps} />
          ) : (
            <div>
              {!isNoPaymentFlow && shouldShowHeader && (
                <AuthAndPaymentFlowHeader
                  header={header}
                  subheader={subheader}
                  billingInfo={billingInfo}
                />
              )}
              {billingInfo && !hasRecurlyError && (
                <PaymentInformationHeader billingInformation={billingInfo} />
              )}
              <PaymentOptionsWrapper
                {...paymentProps}
                {...this.props}
                selectedProductSku={selectedProductSku}
                setSelectedProductSku={this.setSelectedProductSku}
                isFormDisabled={isFormDisabled}
                disableCreditCardForm={this.disableCreditCardForm}
                logSubscribeActivity={actions.logSubscribeActivity}
              />
            </div>
          )}
          {successDialogContent && (
            <PaymentSuccessDialog
              dataTestId={`${formId}SuccessDialog`}
              shouldShow={showSuccessDialog}
              handleClose={this.closeSuccessDialog}
              content={getLocalizedText(successDialogContent)}
              altHeading={altSuccessDialogHeading}
            />
          )}
          {failureMessageToUse && (
            <OkDialog
              dataTestId={`${formId}FailureDialog`}
              shouldShow={showFailureDialog}
              handleClose={this.closeFailureDialog}
              content={getLocalizedText(failureMessageToUse)}
            />
          )}
        </div>
      </PaymentFlowFormLayout>
    );
  }
}

function mapDispatchToProps(dispatch) {
  const actions = {
    logClientError,
    logSubscribeActivity,
    clearFakeAuthData,
  };

  return {
    actions: bindActionCreators(actions, dispatch),
  };
}

export default flow(
  withRouter,
  connect(null, mapDispatchToProps),
  withAnimationControl,
)(PaymentForm);
